// Shared, dependency-free (no React/DOM import) utilities for the Tiptap
// JSON (ProseMirror) documents that replaced the old hand-rolled Markdown
// dialect as the storage format for task descriptions, comments, and retro
// card bodies. Usable from the Worker (validation, reads) and, if ever
// useful, a standalone script.
//
// This is the one source of truth both the backend validation (this file)
// and the frontend editor's extension list (src/rich-text-editor.tsx) build
// from -- keep ALLOWED_NODE_TYPES/ALLOWED_MARK_TYPES and the mention
// plain-text convention ("@Label") in sync with that file rather than
// letting the two drift.

import { markdownToDoc, type DocMember } from "./markdown-to-doc";

export type TiptapMark = { attrs?: Record<string, unknown>; type: string };
export type TiptapNode = {
  attrs?: Record<string, unknown>;
  content?: TiptapNode[];
  marks?: TiptapMark[];
  text?: string;
  type: string;
};
export type TiptapDoc = { content: TiptapNode[]; type: "doc" };

export const ALLOWED_NODE_TYPES = new Set([
  "doc", "paragraph", "text", "heading", "blockquote", "codeBlock",
  "horizontalRule", "bulletList", "orderedList", "listItem",
  "table", "tableRow", "tableHeader", "tableCell",
  "hardBreak", "mention", "image",
]);

// Inline images are S3-backed (see s3-attachments.ts) and always rendered
// through the stable, permanently-valid inline-download redirect endpoint
// (tasks-api.ts) rather than a raw (expiring) presigned S3 URL, so a stored
// doc's images keep working indefinitely -- an `image` node's src is only
// ever trusted if it matches one of those two known-safe shapes.
const isValidImageSrc = (src: unknown): src is string =>
  typeof src === "string" && (/^https:\/\//i.test(src) || src.startsWith("/api/attachments/inline-download/"));
export const ALLOWED_MARK_TYPES = new Set(["bold", "italic", "strike", "code", "link"]);

// A block-level node's children are themselves block-level nodes for these
// container types (blockquote/listItem/tableCell/tableHeader), everything
// else that carries `content` holds inline nodes (paragraph, heading) --
// matters only for docToPlainText's "insert a line break after this block"
// decision below, not for validation.
const BLOCK_LEVEL_TYPES = new Set([
  "paragraph", "heading", "blockquote", "codeBlock",
  "listItem", "tableCell", "tableHeader",
]);

export const isValidTiptapDoc = (value: unknown): value is TiptapDoc =>
  Boolean(value) && typeof value === "object" && (value as TiptapDoc).type === "doc"
  && Array.isArray((value as TiptapDoc).content);

// Strips node/mark types this app doesn't know about, defensively, rather
// than rejecting a whole payload over one bad field -- keeps the API
// resilient to minor client/server version skew instead of hard-failing.
const sanitizeMarks = (marks: TiptapMark[] | undefined): TiptapMark[] | undefined => {
  if (!marks?.length) return undefined;
  const kept = marks.filter((mark) => ALLOWED_MARK_TYPES.has(mark.type));
  return kept.length ? kept : undefined;
};

const sanitizeNodes = (nodes: TiptapNode[] | undefined): TiptapNode[] => {
  if (!nodes?.length) return [];
  const result: TiptapNode[] = [];
  for (const node of nodes) {
    if (!ALLOWED_NODE_TYPES.has(node.type)) continue;
    if (node.type === "text") {
      if (typeof node.text === "string" && node.text.length) {
        result.push({ marks: sanitizeMarks(node.marks), text: node.text, type: "text" });
      }
      continue;
    }
    if (node.type === "mention") {
      if (typeof node.attrs?.id === "string") {
        result.push({ attrs: { id: node.attrs.id, label: typeof node.attrs.label === "string" ? node.attrs.label : "" }, type: "mention" });
      }
      continue;
    }
    if (node.type === "hardBreak" || node.type === "horizontalRule") {
      result.push({ type: node.type });
      continue;
    }
    if (node.type === "image") {
      if (isValidImageSrc(node.attrs?.src)) {
        result.push({
          attrs: {
            alt: typeof node.attrs?.alt === "string" ? node.attrs.alt : undefined,
            src: node.attrs.src,
            title: typeof node.attrs?.title === "string" ? node.attrs.title : undefined,
          },
          type: "image",
        });
      }
      continue;
    }
    const children = sanitizeNodes(node.content);
    result.push({
      attrs: node.attrs,
      content: children.length ? children : undefined,
      type: node.type,
    });
  }
  return result;
};

/** Strips unknown node/mark types; does not enforce length limits (see parseRichTextField). */
export const sanitizeDoc = (doc: TiptapDoc): TiptapDoc => ({ content: sanitizeNodes(doc.content), type: "doc" });

/** Single-paragraph doc wrapping raw text verbatim -- last-resort fallback, not the general legacy-read path (see safeParseDoc). */
export const wrapPlainTextAsDoc = (text: string): TiptapDoc => ({
  content: [{ content: text ? [{ text, type: "text" }] : undefined, type: "paragraph" }],
  type: "doc",
});

/**
 * Every read site that returns a description/comment/card body funnels the
 * raw stored TEXT column value through this. Tries JSON.parse first (the
 * post-migration/new-write shape); on failure, treats `raw` as legacy
 * Markdown text and runs it through the *same structural converter* the
 * one-time batch migration uses (markdownToDoc), not a lossy plain-text
 * wrap -- this is what lets old, not-yet-migrated rows keep rendering with
 * full fidelity indefinitely, removing the need for a single synchronized
 * cutover instant. wrapPlainTextAsDoc is reserved for the rare case even
 * that structural conversion throws.
 */
export const safeParseDoc = (raw: string | null, members: DocMember[] = []): TiptapDoc | null => {
  if (raw === null || raw === "") return null;
  try {
    const parsed = JSON.parse(raw);
    if (isValidTiptapDoc(parsed)) return sanitizeDoc(parsed);
  } catch {
    // fall through to legacy-Markdown handling below
  }
  try {
    return markdownToDoc(raw, members);
  } catch {
    return wrapPlainTextAsDoc(raw);
  }
};

/**
 * Plain-text extraction shared by: history-edit snippets, LLM prompt
 * building (AI refinement, AI action suggestions), and client-side
 * character counting parity. The mention convention ("@Label") here MUST
 * match the editor's Mention.renderText exactly, or client-side counts and
 * server-side validation can disagree on borderline-length content.
 */
export const docToPlainText = (doc: TiptapDoc | null | undefined): string => {
  if (!doc) return "";
  const parts: string[] = [];
  const walk = (node: TiptapNode) => {
    if (node.type === "text" && typeof node.text === "string") { parts.push(node.text); return; }
    if (node.type === "hardBreak") { parts.push("\n"); return; }
    if (node.type === "mention") { parts.push(`@${(node.attrs?.label as string | undefined) ?? (node.attrs?.id as string | undefined) ?? ""}`); return; }
    for (const child of node.content ?? []) walk(child);
    if (BLOCK_LEVEL_TYPES.has(node.type)) parts.push("\n");
  };
  for (const child of doc.content) walk(child);
  return parts.join("").replace(/\n{3,}/g, "\n\n").trim();
};

/** Walks the doc collecting every mention node's user id (deduped). */
export const extractMentionIds = (doc: TiptapDoc | null | undefined): string[] => {
  const ids = new Set<string>();
  const walk = (node: TiptapNode) => {
    if (node.type === "mention" && typeof node.attrs?.id === "string") ids.add(node.attrs.id);
    for (const child of node.content ?? []) walk(child);
  };
  if (doc) for (const child of doc.content) walk(child);
  return [...ids];
};

/**
 * Truncates a doc tree so its extracted plain text is at most `maxChars`
 * long -- mirrors the old optionalText/cleanText's "silently truncate,
 * mid-word is fine" behavior, tree-aware instead of a raw string .slice().
 * A mention counts as its rendered "@Label" length (matching
 * docToPlainText), a hardBreak counts as 1 char (matching the "\n" it
 * contributes there).
 */
export const truncateDocToPlainTextLimit = (doc: TiptapDoc, maxChars: number): { doc: TiptapDoc | null; truncated: boolean } => {
  let remaining = maxChars;
  let truncated = false;

  const walkNodes = (nodes: TiptapNode[]): TiptapNode[] => {
    const result: TiptapNode[] = [];
    for (const node of nodes) {
      if (remaining <= 0) { truncated = true; break; }
      if (node.type === "text" && typeof node.text === "string") {
        if (node.text.length <= remaining) {
          result.push(node);
          remaining -= node.text.length;
        } else {
          result.push({ ...node, text: node.text.slice(0, remaining) });
          remaining = 0;
          truncated = true;
        }
        continue;
      }
      if (node.type === "mention") {
        const label = `@${(node.attrs?.label as string | undefined) ?? ""}`;
        if (label.length > remaining) { truncated = true; break; }
        result.push(node);
        remaining -= label.length;
        continue;
      }
      if (node.type === "hardBreak") {
        result.push(node);
        remaining -= 1;
        continue;
      }
      const children = walkNodes(node.content ?? []);
      result.push({ ...node, content: node.content ? children : undefined });
    }
    return result;
  };

  const content = walkNodes(doc.content);
  if (!content.length) return { doc: null, truncated: true };
  return { doc: { content, type: "doc" }, truncated };
};

// docToPlainText deliberately contributes no text for an image (it isn't
// text), so the "is this doc actually empty" check below can't rely on
// plain-text length alone -- a description/comment that's only an image
// would otherwise get silently discarded as "empty" and nulled out.
const docHasImage = (nodes: TiptapNode[]): boolean =>
  nodes.some((node) => node.type === "image" || docHasImage(node.content ?? []));

export interface ParseRichTextOptions {
  maxJsonBytes?: number; // defense-in-depth ceiling against pathological structure; defaults to a generous multiple of maxPlainTextChars
  maxPlainTextChars: number;
  required: boolean; // true => cleanText-style (error if empty); false => optionalText-style (empty => null)
}
export type ParseRichTextResult =
  | { doc: TiptapDoc | null; ok: true }
  | { error: string; ok: false };

/**
 * The JSON-doc equivalent of the old optionalText/cleanText validators.
 * Client-supplied `value` is expected to already be a parsed JS object
 * (parseJson() in tasks-api.ts/portal-api.ts already parses the whole
 * request body, so body.description arrives as an object, not a
 * doubly-encoded string).
 */
export const parseRichTextField = (value: unknown, opts: ParseRichTextOptions): ParseRichTextResult => {
  if (value === null || value === undefined || value === "") {
    return opts.required ? { error: "This field is required.", ok: false } : { doc: null, ok: true };
  }
  if (!isValidTiptapDoc(value)) {
    return opts.required ? { error: "Provide valid content.", ok: false } : { doc: null, ok: true };
  }
  let doc = sanitizeDoc(value);

  const maxJsonBytes = opts.maxJsonBytes ?? opts.maxPlainTextChars * 20;
  if (JSON.stringify(doc).length > maxJsonBytes) {
    return { error: "Content is too large.", ok: false };
  }

  if (docToPlainText(doc).length > opts.maxPlainTextChars) {
    doc = truncateDocToPlainTextLimit(doc, opts.maxPlainTextChars).doc ?? { content: [], type: "doc" };
  }

  if (!docToPlainText(doc).trim() && !docHasImage(doc.content)) {
    return opts.required ? { error: "This field is required.", ok: false } : { doc: null, ok: true };
  }
  return { doc, ok: true };
};
